// The second factor at sign-in (decision 20, QA-093): after the first factor, when the person has
// two-factor on. An app code, a text code to their verified mobile, or a recovery code; and
// "Remember this device for 30 days". The words are the sign-in prototype's (site/prototypes/sign-in);
// the card's brand and banner stay the sign-in page's, the title is the step's.

import { useState, type FormEvent } from 'react';
import { RpcError, type TwoFactorMethod } from '@flowology/client';
import { client } from '../api.ts';
import { TWO_FACTOR_LABEL, codeLooksRight, firstMethod } from './signin-model.ts';
import { useDesign } from '../blueprint/design.ts';
import './signin.css';

export function TwoFactorStep({
  methods,
  tenant,
  company,
  onSignedIn,
  onCancel,
}: {
  methods: readonly TwoFactorMethod[];
  /** Whose sign-in this is, under the title. */
  company?: string;
  /** Named off a company host (local development); on one, the host says. */
  tenant?: string;
  onSignedIn: (csrf: string, tenant: string, recoveryCodesLeft?: number) => void;
  onCancel: () => void;
}) {
  const [method, setMethod] = useState<TwoFactorMethod>(firstMethod(methods));
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(false);
  const [texted, setTexted] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fail = (e: unknown) =>
    setError(e instanceof RpcError ? e.message : 'Could not reach Flowology. Check your connection.');

  async function sendText() {
    setBusy(true);
    setError(null);
    try {
      const r = await client.twoFactorText(tenant);
      setTexted(r.phoneTail);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!codeLooksRight(method, code)) return setError(TWO_FACTOR_LABEL[method].hint);
    setBusy(true);
    setError(null);
    try {
      const r = await client.twoFactorVerify({
        method,
        code: code.replace(/\s+/g, ''),
        remember,
        ...(tenant ? { tenant } : {}),
      });
      onSignedIn(r.csrf, r.tenant, r.recoveryCodesLeft);
    } catch (err) {
      setCode('');
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  const label = TWO_FACTOR_LABEL[method];
  // in Blueprint the sign-in sheet already names the company beside its initials, above this title
  const bp = useDesign() === 'blueprint';
  return (
    <form className="login-form" onSubmit={(e) => void submit(e)} aria-label="Second step">
      <h1>{label.title}</h1>
      {company && !bp && <p className="lede login-lede">to {company}</p>}
      <p className="lede">
        {method === 'sms' && texted
          ? `We’ve texted a code to the mobile ending ${texted}. It works for 5 minutes.`
          : label.lede}
      </p>
      {error && (
        <p className="banner bad login-banner" role="alert">
          {error}
        </p>
      )}
      {method === 'sms' && !texted ? (
        <button type="button" className="btn primary wide lg" disabled={busy} onClick={() => void sendText()}>
          {busy ? 'Sending…' : 'Text me a code'}
        </button>
      ) : (
        <>
          <label>
            {label.field}
            <input
              className="field"
              inputMode={method === 'recovery' ? 'text' : 'numeric'}
              autoComplete={method === 'recovery' ? 'off' : 'one-time-code'}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              required
            />
          </label>
          <label className="checkrow">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            Remember this device for 30 days
          </label>
          <button className="btn primary wide lg" disabled={busy}>
            {busy ? 'Checking…' : 'Continue'}
          </button>
        </>
      )}
      <div className="login-other" role="group" aria-label="Other ways to confirm it’s you">
        {methods
          .filter((m) => m !== method)
          .map((m) => (
            <button
              key={m}
              type="button"
              className="lnk"
              onClick={() => {
                setMethod(m);
                setCode('');
                setError(null);
                if (m === 'sms' && !texted) void sendText(); // asked for a text: send it
              }}
            >
              {TWO_FACTOR_LABEL[m].switchTo}
            </button>
          ))}
        <span className="small muted">Lost every way in? Ask the owner to reset your two-factor.</span>
        <button type="button" className="lnk" onClick={onCancel}>
          Start again
        </button>
      </div>
    </form>
  );
}
